import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { uploadVideoDirectly } from '../../service/VideoUploadingService';
import VideoService from '../../service/VideoService';
import formatBytes from '../../utils/BytesUtility';
import { secondsToString } from '../../utils/TimeUtil';
import { useTranslation } from 'react-i18next';
import { ToastNotification } from '../Toast/Toast';
import { Storage } from 'aws-amplify';
import * as Sentry from '@sentry/browser';
import UploadDropzone from '../../features/Editor/components/UploadDropzone/UploadDropzone';
import { ScenesType } from '../../../common/apis/CmsAPI';

const MAX_FILES_AMOUNT = 4;

const UploadClipTab = ({
  isVideoUploading = false,
  sceneToUploadInfo = {},
  onUploadDone = () => { },
  toggleClipUploading = () => { },
  folderId = null,
}) => {
  const [uploading, setUploading] = useState(false);
  const [processingFiles, setProcessingFiles] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploadProgress, updateUploadProgress] = useState({});
  const [totalProgress, setTotalProgress] = useState({});
  const [uploadStartedTimePoint, setUploadStartedTimePoint] = useState(null);
  const [approxTime, setApproxTime] = useState(0);
  const [uploadedScenesData, setUploadedScenesData] = useState([]);
  const { t } = useTranslation();
  const mediaMatch = window.matchMedia('(max-width: 900px)');
  const [isMobileView] = useState(mediaMatch.matches);
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  const progressTitle = useMemo(() => {
    if (totalProgress.percent === 100) {
      if (isVideoUploading) {
        return t('extension.uploading.generating_video_url');
      }

      if (files.length > 1) {
        return t('clip_organizer.add_modal.video_is_processing_message_videos');
      }

      return t('clip_organizer.add_modal.video_is_processing_message');
    }

    return t('clip_organizer.add_modal.uploading');
  }, [files, totalProgress.percent, isVideoUploading]);

  const mainTitle = useMemo(() => {
    if (sceneToUploadInfo?.storyId) {
      if (isMobileView) {
        return t('clip_organizer.add_modal.instructions_drag_drop_one_video.mobile');
      }

      return t('clip_organizer.add_modal.instructions_drag_drop_one_video');
    }

    if (isMobileView) {
      return t('clip_organizer.add_modal.instructions_drag_drop.mobile');
    }

    return t('clip_organizer.add_modal.instructions_drag_drop');
  }, [isMobileView, sceneToUploadInfo]);

  const handleUploadProgress = ({ percent, loaded, total, videoIndex }) => {
    toggleClipUploading(true);
    updateUploadProgress(prevState => ({ ...prevState, [videoIndex]: { percent, loaded, total } }));
  };

  const handleUploadError = error => {
    setUploading(false);
    toggleClipUploading(false);
    setShowErrorMessage(true);
    setFiles([]);
    Sentry.captureException(error);
  };

  useEffect(() => {
    const doUpload = async (file, index) => {
      try {
        // We have to pass the file to the backend in order for us to upload it through Amplify
        // For this we have to send a message, but these messages only support JSON serialized strings
        // In order to send a file to the backend, we therefore need to build an object URL and send that
        // @see: https://stackoverflow.com/a/66897859/1800213

        const uploadVideo = async (resultFile) => {
          // Send a message to the backend to start uploading
          let fileName = file.name;
          let fileExtension = fileName.substr(fileName.lastIndexOf('.'), fileName.length);
          const preparedFileName = fileName.replace(fileExtension, '');

          if (sceneToUploadInfo.sceneId) {
            try {
              const videoKey = await VideoService.uploadVideoToAws({
                video: resultFile,
                videoExtension: fileExtension,
                onUploadProgress: handleUploadProgress,
                draftStoryId: sceneToUploadInfo?.storyId,
                draftSceneId: sceneToUploadInfo?.sceneId,
              });

              const fullPath = await Storage.get(videoKey, { level: 'protected' });
              await VideoService.updateScene(sceneToUploadInfo.sceneId, {
                storyId: sceneToUploadInfo.storyId,
                video: fullPath,
                trimRanges: [
                  {
                    from: 0,
                    to: 0,
                  },
                ],
              });

              setUploadedScenesData(prevState => [...prevState, sceneToUploadInfo.storyId]);
            } catch (error) {
              handleUploadError(error);
            }
          } else {
            try {
              const story = await uploadVideoDirectly({
                videoBlob: resultFile,
                videoName: preparedFileName,
                videoExtension: fileExtension,
                onUploadProgress: handleUploadProgress,
                videoIndex: index,
              });

              console.log('story after uploadVideoDirectly', story);

              toggleClipUploading(false);
              setUploadedScenesData(prevState => [...prevState, story.storyId]);
            } catch (error) {
              handleUploadError(error);
            }
          }
        };

        await uploadVideo(file);
      } catch (e) {
        handleUploadError(e);
      }
    };

    const uploadSeveralFiles = async () => {
      await Promise.all(
        files.map(async (file, index) => {
          return await doUpload(file, index);
        })
      );
    };

    if (uploading && files.length > 0) {
      setShowErrorMessage(false);
      uploadSeveralFiles();
    }
  }, [files, uploading]);

  useEffect(() => {
    if (!uploadStartedTimePoint) {
      setUploadStartedTimePoint(Date.now());
    }
    if (Object.keys(uploadProgress).length > 0) {
      let percent = 0;
      let total = 0;
      let loaded = 0;
      for (const key in uploadProgress) {
        total += uploadProgress[key].total;
        loaded += uploadProgress[key].loaded;
      }
      percent = (loaded / total) * 100;
      setTotalProgress({ percent, total: formatBytes(total), loaded: formatBytes(loaded) });
    }
  }, [uploadProgress]);

  useEffect(() => {
    if (Object.keys(totalProgress).length > 0) {
      const elapsedTime = Date.now() - uploadStartedTimePoint;
      const newApproxTime = Math.round((elapsedTime / totalProgress.percent) * (100 - totalProgress.percent));
      if (elapsedTime % 4 === 0) {
        setApproxTime(secondsToString(newApproxTime / 1000));
      }
    }
  }, [totalProgress]);

  useEffect(() => {
    const checkUploads = async uploadedScenesData => {
      if (uploadedScenesData.length && files) {
        setProcessingFiles(true);

        const requestedScene = await Promise.all(
          uploadedScenesData.map(async item => {
            const details = await VideoService.getVideoDetails(item, ScenesType.REGULAR);
            if (details.scenes) {
              if (folderId) {
                await VideoService.updateVideo(details.id, { folder: folderId });
              }

              if (sceneToUploadInfo.storyId === details.id) {
                const uploadedScene = details.scenes?.find(item => item.id === sceneToUploadInfo.sceneId && item.video);
                if (uploadedScene) {
                  return { ...uploadedScene, author: details.author, storyId: details.id, source: 'uploaded' };
                }

                return null;
              }

              return { ...details.scenes[0], author: details.author, storyId: details.id, source: 'uploaded' };
            }

            return null;
          })
        );

        const renderedScenes = requestedScene.filter(item => item !== null).map(item => ({ ...item, uploaded: true }));
        if (renderedScenes.length !== uploadedScenesData.length) {
          setTimeout(() => {
            checkUploads(uploadedScenesData);
          }, 3000);
        }

        if (files.length > 0 && renderedScenes.length === files.length) {
          toggleClipUploading(false);

          setTimeout(() => {
            setProcessingFiles(false);
            onUploadDone(renderedScenes);
          }, 10);
        }
      }
    };

    checkUploads(uploadedScenesData);
  }, [uploadedScenesData]);

  useEffect(() => {
    return () => {
      setUploading(false);
      setProcessingFiles(false);
      setFiles([]);
      setUploadedScenesData([]);
    };
  }, []);

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (sceneToUploadInfo.sceneId) {
      if (acceptedFiles.length > 1) {
        ToastNotification({
          message: t('clip_organizer.add_modal.warning_one_video'),
          isMobileView: isMobileView,
        });

        return;
      }

      const fullFileName = acceptedFiles[0].name;
      const fileExtension = fullFileName.substr(fullFileName.lastIndexOf('.'), fullFileName.length);

      // At the moment "VideoService.updateScene()" requires video duration for trimRange
      // The .avi and .wmv formats do not allow get this value,
      // so we should avoid these formats when uploading video directly into the scene
      if (['.avi', '.wmv'].includes(fileExtension)) {
        ToastNotification({
          message: t('clip_organizer.add_modal.warning_one_video.wrong_format'),
          isMobileView: isMobileView,
        });

        return;
      }
    }

    if (acceptedFiles.length + rejectedFiles.length > MAX_FILES_AMOUNT) {
      ToastNotification({
        message: t('clip_organizer.add_modal.files_simultaneously', {
          number: MAX_FILES_AMOUNT,
        }),
        isMobileView: isMobileView,
      });
      return;
    }

    rejectedFiles.forEach(({ file }) => {
      ToastNotification({
        message: `${file.name} ${t('clip_organizer.add_modal.wrong_file_format')} ${t('clip_organizer.add_modal.supported_files')}`,
        isMobileView: isMobileView,
      });
    });

    const resultFiles = acceptedFiles.filter(acceptedFile => {
      if (acceptedFile.size > 4294967296) {
        ToastNotification({
          message: `${acceptedFile.name} ${t('clip_organizer.add_modal.file_is_too_large')} ${t(
            'clip_organizer.add_modal.supported_files'
          )}`,
          isMobileView: isMobileView,
        });
        return;
      }

      setFiles(prevState => [...prevState, acceptedFile]);
      return acceptedFile;
    });

    // Should be used 'resultFiles' instead of the 'files' that we set as,
    // since iterating through the array is very fast and the value of the files may be empty, although later it will not be empty
    if (resultFiles.length) {
      setUploading(true);
    }
  }, []);

  return (
    <UploadDropzone
      mainTitle={mainTitle}
      progressTitle={progressTitle}
      onDrop={onDrop}
      isUploading={uploading}
      isProcessingFiles={processingFiles}
      totalProgress={totalProgress}
      approxTime={approxTime}
      showErrorMessage={showErrorMessage}
      maxFiles={MAX_FILES_AMOUNT} />
  );
};

export default UploadClipTab;
